import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  User as FirebaseUser,
} from "firebase/auth";
import { auth } from "../firebase";

const API = process.env.REACT_APP_API_URL ?? "http://localhost:4000";

// ── Types ─────────────────────────────────────────────────
export type UserRole = "client" | "dietitian";

export type AppUser = {
  uid:           string;
  name:          string;
  email:         string;
  role:          UserRole;
  status:        "pending" | "active" | "inactive";
  weight?:       number;
  // add more profile fields here as needed
};

type AuthContextType = {
  user:         AppUser | null;
  firebaseUser: FirebaseUser | null;
  isLoggedIn:   boolean;
  loading:      boolean;
  register:     (name: string, email: string, password: string) => Promise<AppUser>;
  login:        (email: string, password: string) => Promise<AppUser>;
  logout:       () => Promise<void>;
  error:        string | null;
};

// ── Context ───────────────────────────────────────────────
const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user,         setUser]         = useState<AppUser | null>(null);
  const [firebaseUser, setFirebaseUser] = useState<FirebaseUser | null>(null);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState<string | null>(null);

  // ── Listen for Firebase auth state changes ──────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (fbUser: FirebaseUser | null) => {
      setFirebaseUser(fbUser);

      if (fbUser) {
        try {
          const idToken = await fbUser.getIdToken();
          const res = await fetch(`${API}/api/auth/me`, {
            headers: { Authorization: `Bearer ${idToken}` },
          });
          if (res.ok) {
            const data = await res.json();
            setUser(data.user);
          } else {
            setUser(null);
          }
        } catch {
          setUser(null);
        }
      } else {
        setUser(null);
      }

      setLoading(false);
    });

    return unsub;
  }, []);

  // ── Register ──────────────────────────────────────────
  const register = async (name: string, email: string, password: string): Promise<AppUser> => {
    setError(null);
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password);
      const idToken = await cred.user.getIdToken();

      const res = await fetch(`${API}/api/auth/register`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ idToken, name }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error ?? "Registration failed");
      }

      const data = await res.json();
      const appUser: AppUser = data.user;
      setUser(appUser);
      await cred.user.getIdToken(true);
      return appUser;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Registration failed";
      setError(msg);
      throw err;
    }
  };

  // ── Login ─────────────────────────────────────────────
  const login = async (email: string, password: string): Promise<AppUser> => {
    setError(null);
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const idToken = await cred.user.getIdToken();

      const res = await fetch(`${API}/api/auth/login`, {
        method:  "POST",
        headers: { "Content-Type": "application/json" },
        body:    JSON.stringify({ idToken }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error ?? "Login failed");
      }

      const data = await res.json();
      const appUser: AppUser = data.user;
      setUser(appUser);
      return appUser;
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Login failed";
      setError(msg);
      throw err;
    }
  };

  // ── Logout ────────────────────────────────────────────
  const logout = async () => {
    await signOut(auth);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{
      user, firebaseUser,
      isLoggedIn: !!user,
      loading,
      register, login, logout,
      error,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
